import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TENANT_PATHS } from '../../../routing/paths.ts';
import { RiskFormFields, defaultRiskFormValues } from '../components/RiskFormFields.tsx';
import { useRiskById, useUpdateRisk } from '../../../hooks/risks/useRisks.ts';
import { useAuditYear } from '../../../contexts/AuditYearContext.tsx';
import type { RiskRecord } from '../../../request/risks/risks.api.ts';
import { riskManualEditSchema, type RiskFormValues } from '../schema.ts';
import type { RiskUpsertInput } from '../../../request/risks/risks.types.ts';
import {
  buildChangedPatch,
  buildControlLabelOptionsFromRisk,
  extractProcessAssociations,
  normalizeFormForPatch,
} from '../../../request/risks/functional.js';
import type { ProcessCardContext } from '../components/RiskAssociationsEditor.tsx';
import { RiskCreatePanel } from '../components/RiskCreatePanel.tsx';
import ReadOnlyBanner from '../../../components/ReadOnlyBanner.tsx';
import { RiskDetailsHeader } from '../components/RiskDetailsHeader.tsx';
import { InlineEditActions } from '../../../shared/common/InlineEditActions.tsx';
import {
  RiskDetailsErrorState,
  RiskDetailsLoadingState,
  RiskDetailsNotFoundState,
} from '../components/RiskDetailsStates.tsx';
import {
  asText,
  formatAuditTimestamp,
  formatRiskSourceLabel,
  resolveMetaBarSystemRiskId,
} from '../utils/riskDetailsUtils.ts';
import type { OptionLabel } from '../../../types/options.ts';

interface RiskDetailsPageProps {
  mode: 'create' | 'edit' | 'view';
}

function decodeRouteRiskId(raw: string | undefined): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

const RiskDetailsPage = ({ mode }: RiskDetailsPageProps) => {
  const { riskId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState<RiskRecord | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<RiskFormValues>(defaultRiskFormValues);
  const { selectedAuditYearId, selectedAuditYear } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);
  const updateRiskMutation = useUpdateRisk();
  const riskQuery = useRiskById(mode !== 'create' ? riskId : undefined);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<RiskFormValues>({
    resolver: yupResolver(riskManualEditSchema),
    defaultValues: defaultRiskFormValues,
  });

  const goBackToList = useCallback(() => navigate(TENANT_PATHS.SOX_RISKS), [navigate]);

  const startEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  useEffect(() => {
    if (mode === 'create') return;
    if (riskQuery.isLoading || riskQuery.isError || !riskQuery.data) return;
    const found = riskQuery.data;
    const foundRecord = found as unknown as Record<string, unknown>;
    const processAssociations = extractProcessAssociations(foundRecord);
    setRecord(found);
    const classificationRaw = String(
      found.classification ?? foundRecord.classification ?? 'business'
    )
      .trim()
      .toLowerCase();
    const classification = classificationRaw === 'itgc' ? 'itgc' : 'business';
    const itgcDomainObj =
      (foundRecord.itgc_domain as { id?: string } | undefined) ??
      (foundRecord.itgcDomain as { id?: string } | undefined);
    const itgcDomainId = asText(
      foundRecord.itgc_domain_id ?? foundRecord.itgcDomainId ?? itgcDomainObj?.id ?? ''
    );
    const nextValues = normalizeFormForPatch(
      {
      clientRiskId: asText(found.clientRiskId ?? found.client_risk_id ?? ''),
      riskDescription: asText(found.riskDescription ?? found.risk_description),
      riskCategory: asText(found.riskCategory || 'SOX'),
      riskLevel: (found.riskLevel ?? found.risk_level ?? 'Medium') as 'High' | 'Medium' | 'Low',
      classification,
      itgcDomainId,
      processAssociations: classification === 'itgc' ? [] : processAssociations,
      },
      riskManualEditSchema
    ) as RiskFormValues;
    setInitialFormValues(nextValues);
    reset(nextValues);
  }, [mode, reset, riskQuery.data, riskQuery.isError, riskQuery.isLoading]);

  const onSubmit = async (values: RiskFormValues) => {
    if (mode !== 'view' || !isEditing) return;
    try {
      const recordUuid = record ? String(record.id) : '';
      if (!recordUuid) return;
      const normalizedValues = normalizeFormForPatch(values, riskManualEditSchema) as RiskFormValues;
      const changedPatch = buildChangedPatch(initialFormValues, normalizedValues) as RiskUpsertInput;
      if (Object.keys(changedPatch).length === 0) {
        goBackToList();
        return;
      }
      await updateRiskMutation.mutateAsync({ riskId: recordUuid, payload: changedPatch, auditYearId: selectedAuditYearId ?? undefined });
      goBackToList();
    } catch {
      // Toast is handled inside the mutation hook; keep the form open for correction/retry.
    }
  };

  const handleCancel = useCallback(() => {
    if (mode === 'view' && isEditing) {
      reset(initialFormValues);
      setIsEditing(false);
      return;
    }
    goBackToList();
  }, [goBackToList, initialFormValues, isEditing, mode, reset]);

  const isSaving = updateRiskMutation.isPending;

  const updatedLabel = useMemo(() => formatAuditTimestamp(record?.updated_at) || '-', [record?.updated_at]);
  /**
   * Labels keyed the same way as `/api/processes` and `extractProcessAssociations`
   * (`id` / flowchart UUID before route `process_id`). Merges `processes[]` then
   * `process_mappings` rows so names always come from the loaded risk payload.
   */
  const initialProcessOptions = useMemo<OptionLabel[]>(() => {
    if (!record) return [];
    const raw = record as unknown as Record<string, unknown>;

    const rowToOptions = (process: Record<string, unknown>): OptionLabel[] => {
      const wireId = String(
        process.process_uuid ?? process.processUuid ?? process.id ?? ''
      ).trim();
      const routeProcessId = String(process.process_id ?? process.processId ?? '').trim();
      const pickerValue = wireId || routeProcessId;
      if (!pickerValue) return [];
      const processName = String(process.process_name ?? process.name ?? routeProcessId).trim();
      const cycleId = String(process.cycle_id ?? '').trim();
      const cycleName = String(process.cycle_name ?? process.sox_cycle_name ?? '').trim();
      const labelCore = cycleId
        ? `${cycleId} - ${routeProcessId} - ${processName}`
        : routeProcessId
          ? `${routeProcessId} - ${processName}`
          : processName;
      const label = cycleName ? `${labelCore} (${cycleName})` : labelCore;
      const primary: OptionLabel = { value: pickerValue, label };
      const aliases: OptionLabel[] = [];
      if (wireId && wireId !== pickerValue) aliases.push({ value: wireId, label });
      if (routeProcessId && routeProcessId !== pickerValue && routeProcessId !== wireId) {
        aliases.push({ value: routeProcessId, label });
      }
      return [primary, ...aliases];
    };

    const seen = new Set<string>();
    const out: OptionLabel[] = [];
    const pushUnique = (opts: OptionLabel[]) => {
      for (const o of opts) {
        if (!o.value || seen.has(o.value)) continue;
        seen.add(o.value);
        out.push(o);
      }
    };

    const processes = Array.isArray(raw.processes) ? raw.processes : [];
    processes.forEach((row) => pushUnique(rowToOptions(row as Record<string, unknown>)));

    const mappingRows = [
      ...(Array.isArray(raw.process_mappings) ? raw.process_mappings : []),
      ...(Array.isArray(raw.processMappings) ? raw.processMappings : []),
    ];
    mappingRows.forEach((row) => pushUnique(rowToOptions(row as Record<string, unknown>)));

    return out;
  }, [record]);
  const initialControlOptions = useMemo<OptionLabel[]>(() => {
    if (!record) return [];
    return buildControlLabelOptionsFromRisk(record as RiskRecord);
  }, [record]);

  const controlClassification = useMemo(() => {
    if (!record) return 'business';
    const raw = record as unknown as Record<string, unknown>;
    const value = String(record.classification ?? raw.classification ?? 'business').trim().toLowerCase();
    return value === 'itgc' ? 'itgc' : 'business';
  }, [record]);

  const itgcDomainLabel = useMemo(() => {
    if (!record) return '';
    const raw = record as unknown as Record<string, unknown>;
    const domain =
      (raw.itgc_domain as { code?: string; name?: string } | undefined) ??
      (raw.itgcDomain as { code?: string; name?: string } | undefined);
    if (!domain) return '';
    return [String(domain.code ?? '').trim(), String(domain.name ?? '').trim()].filter(Boolean).join(' - ');
  }, [record]);

  const processContextByUuid = useMemo(() => {
    if (!record) return undefined;
    const raw = record as unknown as Record<string, unknown>;
    const out: Record<string, ProcessCardContext> = {};

    const addContextRow = (p: Record<string, unknown>) => {
      const key = String(
        p.id ?? p.process_uuid ?? p.processUuid ?? p.process_id ?? p.processId ?? ''
      ).trim();
      if (!key || out[key]) return;
      const vn = p.flowchart_version_number ?? p.version_number ?? p.version;
      const versionLabel =
        vn != null && vn !== '' ? `v${vn}` : undefined;
      const statusRaw = p.version_status ?? p.flowchart_version_status ?? p.status ?? '';
      const statusLabel = statusRaw !== '' ? String(statusRaw).toLowerCase() : undefined;
      out[key] = {
        cycleId: String(p.cycle_id ?? p.cycleId ?? '').trim(),
        routeProcessId: String(p.process_id ?? p.processId ?? '').trim(),
        versionLabel,
        statusLabel,
      };
    };

    const processes = Array.isArray(raw.processes) ? raw.processes : [];
    processes.forEach((row) => addContextRow(row as Record<string, unknown>));

    const mappingRows = [
      ...(Array.isArray(raw.process_mappings) ? raw.process_mappings : []),
      ...(Array.isArray(raw.processMappings) ? raw.processMappings : []),
    ];
    mappingRows.forEach((row) => addContextRow(row as Record<string, unknown>));

    return Object.keys(out).length > 0 ? out : undefined;
  }, [record]);

  if (mode !== 'create' && riskQuery.isLoading) return <RiskDetailsLoadingState />;
  if (mode !== 'create' && riskQuery.isError) {
    return <RiskDetailsErrorState onRetry={() => riskQuery.refetch()} onBack={goBackToList} />;
  }
  if (mode !== 'create' && riskQuery.isSuccess && !riskQuery.data) {
    return <RiskDetailsNotFoundState onBack={goBackToList} />;
  }
  if (mode !== 'create' && !record) return null;

  /** Sticky header: always `Risk {id}` using URL segment (e.g. RSK_000025), then loaded record system id. */
  const headerSubtitle =
    mode === 'create' ? '' : decodeRouteRiskId(riskId) || (record ? resolveMetaBarSystemRiskId(record) : '');
  const headerDescription = mode !== 'create' && record
    ? asText(record.riskDescription ?? (record as unknown as Record<string, unknown>).risk_description)
    : '';
  const metaRiskSystemId = record ? resolveMetaBarSystemRiskId(record) : '';
  const source = record?.source ? String(record.source) : '';
  const sourceLabel = formatRiskSourceLabel(source);
  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="max-w-5xl mx-auto px-8 pt-4">
        <ReadOnlyBanner />
      </div>
      <RiskDetailsHeader
        mode={mode === 'view' && isEditing ? 'edit' : mode}
        subtitle={headerSubtitle}
        description={headerDescription}
        onBack={goBackToList}
      />

      <div className="max-w-5xl mx-auto px-8 py-8 min-w-0">
        {mode === 'create' ? (
          <RiskCreatePanel />
        ) : mode === 'view' && !isEditing ? (
          <RiskFormFields
            readOnly
            headerActions={
              <InlineEditActions
                isEditing={false}
                onEdit={startEditing}
                editDisabled={isLocked}
                editDisabledReason="This audit year is locked — switch to an active year to edit"
              />
            }
            currentValues={initialFormValues}
            register={register}
            control={control}
            errors={errors}
            initialProcessOptions={initialProcessOptions}
            initialControlOptions={initialControlOptions}
            processContextByUuid={processContextByUuid}
            controlClassification={controlClassification}
            itgcDomainLabel={itgcDomainLabel}
            riskSystemId={metaRiskSystemId}
            sourceLabel={sourceLabel}
            updatedLabel={updatedLabel}
          />
        ) : mode === 'view' && isEditing ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <RiskFormFields
              headerActions={
                <InlineEditActions
                  isEditing
                  isSaving={isSaving}
                  onCancel={handleCancel}
                  saveLabel="Save"
                />
              }
              register={register}
              control={control}
              errors={errors}
              setValue={setValue}
              clearErrors={clearErrors}
              currentValues={initialFormValues}
              initialProcessOptions={initialProcessOptions}
              initialControlOptions={initialControlOptions}
              processContextByUuid={processContextByUuid}
              controlClassification={controlClassification}
              itgcDomainLabel={itgcDomainLabel}
              riskSystemId={metaRiskSystemId}
              sourceLabel={sourceLabel}
              updatedLabel={updatedLabel}
            />
          </form>
        ) : null}
      </div>
    </div>
  );
};

export default RiskDetailsPage;
